import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { orgAPI } from "./api";

import type {
  GetSubscriptionResponse,
  ListBillingPlansResponse,
  ListCreditUsagePerUserResponse,
  ListPaymentMethodsResponse,
  ListStripeInvoicesResponse,
  MeData,
  PreviewStripeInvoicePayload,
  PreviewStripeInvoiceResponse,
  SetupIntentResponse,
  UpdateSubscriptionPayload,
  UpdateSubscriptionResponse,
} from "@cohesive/api-client";
import { useUserInfo } from "./user";

// Context types for optimistic updates
interface SubscriptionMutationContext {
  previousSubscription?: GetSubscriptionResponse;
  previousMeData?: MeData;
  previousPaymentMethods?: ListPaymentMethodsResponse;
  isCancellationOperation?: boolean;
  cancel_at_period_end?: boolean;
}

export function useCreateSetupIntent(
  options?: Partial<UseMutationOptions<SetupIntentResponse, AxiosError>>,
) {
  return useMutation<SetupIntentResponse, AxiosError>({
    mutationKey: ["setupIntent"],
    mutationFn: async () => orgAPI.billing.createSetupIntent(),
    ...options,
  });
}

export function useSetupIntentClientSecret(
  options?: Partial<UseQueryOptions<SetupIntentResponse, AxiosError>>,
) {
  return useQuery<SetupIntentResponse, AxiosError>({
    queryKey: ["setupIntentClientSecret"],
    queryFn: async () => orgAPI.billing.createSetupIntent(),
    ...options,
  });
}

export function useListCreditUsagePerUser(
  options?: Partial<
    UseQueryOptions<ListCreditUsagePerUserResponse, AxiosError>
  >,
) {
  return useQuery<ListCreditUsagePerUserResponse, AxiosError>({
    queryKey: ["creditUsagePerUser"],
    queryFn: async () => orgAPI.billing.listCreditUsagePerUser(),
    ...options,
  });
}

export function useListBillingPlans(
  options?: Partial<UseQueryOptions<ListBillingPlansResponse, AxiosError>>,
) {
  return useQuery<ListBillingPlansResponse, AxiosError>({
    queryKey: ["billingPlans"],
    queryFn: async () => orgAPI.billing.listBillingPlans(),
    ...options,
  });
}

export function useListPaymentMethods(
  options?: Partial<UseQueryOptions<ListPaymentMethodsResponse, AxiosError>>,
) {
  return useQuery<ListPaymentMethodsResponse, AxiosError>({
    queryKey: ["paymentMethods"],
    queryFn: async () => orgAPI.billing.listPaymentMethods(),
    ...options,
  });
}

export function useGetSubscription(
  options?: Partial<UseQueryOptions<GetSubscriptionResponse, AxiosError>>,
) {
  return useQuery<GetSubscriptionResponse, AxiosError>({
    queryKey: ["subscription"],
    queryFn: async () => orgAPI.billing.getSubscription(),
    ...options,
  });
}

export function useUpdateSubscription(
  options?: Partial<
    UseMutationOptions<
      UpdateSubscriptionResponse,
      AxiosError,
      UpdateSubscriptionPayload,
      SubscriptionMutationContext
    >
  >,
) {
  // Ensure userInfo is available - subscription info comes from /me endpoint
  // Note: We don't call useGetSubscription() here as it may 404 for new organizations
  useUserInfo();

  const queryClient = useQueryClient();

  return useMutation<
    UpdateSubscriptionResponse,
    AxiosError,
    UpdateSubscriptionPayload,
    SubscriptionMutationContext
  >({
    mutationKey: ["updateSubscription"],
    mutationFn: async (payload: UpdateSubscriptionPayload) =>
      orgAPI.billing.updateSubscription(payload),
    ...options,
    onMutate: async (payload: UpdateSubscriptionPayload) => {
      // Determine if this is a cancellation/reactivation operation
      const isCancellationOperation =
        payload.cancel_at_period_end === true ||
        payload.cancel_at_period_end === false;

      // Cancel outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: ["subscription"] });
      await queryClient.cancelQueries({ queryKey: ["userInfo"] });
      await queryClient.cancelQueries({ queryKey: ["paymentMethods"] });

      // Snapshot the previous values from cache (don't fetch - may not exist for new orgs)
      // These may be undefined for new organizations without a subscription yet
      const previousSubscription =
        queryClient.getQueryData<GetSubscriptionResponse>(["subscription"]);
      const previousMeData = queryClient.getQueryData<MeData>(["userInfo"]);
      const previousPaymentMethods =
        queryClient.getQueryData<ListPaymentMethodsResponse>([
          "paymentMethods",
        ]);

      // Only apply optimistic updates for non-cancellation/reactivation operations
      if (
        !isCancellationOperation &&
        previousSubscription?.data.stripe_subscription
      ) {
        const currentSub = previousSubscription.data.stripe_subscription;
        let optimisticSub = { ...currentSub, ...payload };

        // Only end the trial optimistically when explicitly requested — a
        // keep_trial card attach sends trial_end_now: false and keeps trialing.
        if (payload.trial_end_now === true) {
          optimisticSub.status = "active";
        }

        const optimisticSubscription: GetSubscriptionResponse = {
          data: {
            stripe_subscription: optimisticSub,
          },
        };

        queryClient.setQueryData(["subscription"], optimisticSubscription);
      }

      // Optimistically update MeData if we have existing data
      if (!isCancellationOperation && previousMeData?.subscription) {
        const currentSub = previousMeData.subscription;
        let optimisticSub = { ...currentSub, ...payload };

        // Only end the trial optimistically when explicitly requested — a
        // keep_trial card attach sends trial_end_now: false and keeps trialing.
        if (payload.trial_end_now === true) {
          optimisticSub.status = "active";
        }

        const optimisticMeData: MeData = {
          ...previousMeData,
          subscription: optimisticSub,
        };

        queryClient.setQueryData(["userInfo"], optimisticMeData);
      }

      // Optimistically update payment methods if changing default payment method
      if (
        payload.default_payment_method_id &&
        previousPaymentMethods?.data.payment_methods
      ) {
        const optimisticPaymentMethods: ListPaymentMethodsResponse = {
          data: {
            payment_methods: previousPaymentMethods.data.payment_methods.map(
              (pm) => ({
                ...pm,
                default: pm.id === payload.default_payment_method_id,
              }),
            ),
          },
        };

        queryClient.setQueryData(["paymentMethods"], optimisticPaymentMethods);
      }

      // Return a context object with the snapshotted values and invalidation flag
      return {
        previousSubscription,
        previousMeData,
        previousPaymentMethods,
        isCancellationOperation,
      };
    },
    onError: (err, payload, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousSubscription) {
        queryClient.setQueryData(
          ["subscription"],
          context.previousSubscription,
        );
      }

      if (context?.previousMeData) {
        queryClient.setQueryData(["userInfo"], context.previousMeData);
      }

      if (context?.previousPaymentMethods) {
        queryClient.setQueryData(
          ["paymentMethods"],
          context.previousPaymentMethods,
        );
      }

      // Call the original onError if provided
      options?.onError?.(err, payload, context);
    },
    onSuccess: (data, payload, context) => {
      // Invalidate and refetch queries to get fresh data
      // This is especially important for:
      // - New subscriptions (no previous data existed)
      // - Cancellation/reactivation operations
      // - Any subscription changes that affect billing state
      // A dead subscription (canceled/incomplete/incomplete_expired) is
      // replaced server-side; the optimistic update can't know the new
      // status, so refetch to pick up the replacement.
      const previousStatus =
        context?.previousSubscription?.data.stripe_subscription?.status;
      const subscriptionReplaced =
        previousStatus === "canceled" ||
        previousStatus === "incomplete" ||
        previousStatus === "incomplete_expired";

      const shouldInvalidate =
        context?.isCancellationOperation ||
        subscriptionReplaced ||
        !context?.previousSubscription ||
        !context?.previousMeData;

      let invalidation: Promise<unknown> = Promise.resolve();
      if (shouldInvalidate) {
        invalidation = Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["subscription"],
            refetchType: "all",
          }),
          queryClient.invalidateQueries({
            queryKey: ["userInfo"],
            refetchType: "all",
          }),
          queryClient.invalidateQueries({
            queryKey: ["paymentMethods"],
            refetchType: "all",
          }),
          queryClient.invalidateQueries({
            queryKey: ["creditUsagePerUser"],
            refetchType: "all",
          }),
        ]);
      }

      // Call the original onSuccess if provided
      options?.onSuccess?.(data, payload, context);

      // Returned promises are awaited before mutateAsync resolves. A new
      // signup has no cached subscription for the optimistic update to touch,
      // so navigation after mutateAsync (ConfirmSetupPage -> HOME) must wait
      // for the /me refetch — otherwise useRequireSubscription reads the stale
      // no-subscription cache and bounces the user back to plan selection.
      return invalidation;
    },
  });
}

export function useListStripeInvoices(
  options?: Partial<UseQueryOptions<ListStripeInvoicesResponse, AxiosError>>,
) {
  return useQuery<ListStripeInvoicesResponse, AxiosError>({
    queryKey: ["stripeInvoices"],
    queryFn: async () => orgAPI.billing.listStripeInvoices(),
    ...options,
  });
}

export function useCancelSubscription(
  options?: Partial<
    UseMutationOptions<
      UpdateSubscriptionResponse,
      AxiosError,
      boolean,
      SubscriptionMutationContext
    >
  >,
) {
  const queryClient = useQueryClient();
  const { data: subscription } = useGetSubscription();

  return useMutation<
    UpdateSubscriptionResponse,
    AxiosError,
    boolean,
    SubscriptionMutationContext
  >({
    mutationKey: ["cancelSubscription"],
    mutationFn: async (cancel_at_period_end: boolean) => {
      const currentSubscription = subscription?.data.stripe_subscription;
      if (!currentSubscription) {
        throw new Error("No active subscription found");
      }

      return orgAPI.billing.updateSubscription({
        plan_name: currentSubscription.plan_name,
        billing_interval: currentSubscription.billing_interval,
        seat_count: currentSubscription.seat_count,
        cancel_at_period_end,
      });
    },
    ...options,
    onMutate: async (cancel_at_period_end: boolean) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["subscription"] });
      await queryClient.cancelQueries({ queryKey: ["userInfo"] });

      // Snapshot the previous values for potential rollback
      const previousSubscription =
        queryClient.getQueryData<GetSubscriptionResponse>(["subscription"]);
      const previousMeData = queryClient.getQueryData<MeData>(["userInfo"]);

      // No optimistic updates in onMutate for cancellation operations
      return {
        previousSubscription,
        previousMeData,
        isCancellationOperation: true,
        cancel_at_period_end,
      };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousSubscription) {
        queryClient.setQueryData(
          ["subscription"],
          context.previousSubscription,
        );
      }

      if (context?.previousMeData) {
        queryClient.setQueryData(["userInfo"], context.previousMeData);
      }

      // Call the original onError if provided
      options?.onError?.(err, variables, context);
    },
    onSuccess: (data, cancel_at_period_end, context) => {
      // Apply optimistic updates after successful API call
      if (context?.previousSubscription?.data.stripe_subscription) {
        const currentSub =
          context.previousSubscription.data.stripe_subscription;
        let updatedSub = { ...currentSub };

        if (cancel_at_period_end === true) {
          // Subscription being cancelled - set cancel_at to period end
          updatedSub.cancel_at = currentSub.current_period_end;
        } else if (cancel_at_period_end === false) {
          // Subscription being reactivated - remove cancel_at
          const { cancel_at, ...subWithoutCancelAt } = updatedSub;
          updatedSub = subWithoutCancelAt;
        }

        const optimisticSubscription: GetSubscriptionResponse = {
          data: {
            stripe_subscription: updatedSub,
          },
        };

        queryClient.setQueryData(["subscription"], optimisticSubscription);
      }

      // Update MeData as well
      if (context?.previousMeData?.subscription) {
        const currentSub = context.previousMeData.subscription;
        let updatedSub = { ...currentSub };

        if (cancel_at_period_end === true) {
          // Subscription being cancelled - set cancel_at to period end
          updatedSub.cancel_at = currentSub.current_period_end;
        } else if (cancel_at_period_end === false) {
          // Subscription being reactivated - remove cancel_at
          const { cancel_at, ...subWithoutCancelAt } = updatedSub;
          updatedSub = subWithoutCancelAt;
        }

        const optimisticMeData: MeData = {
          ...context.previousMeData,
          subscription: updatedSub,
        };

        queryClient.setQueryData(["userInfo"], optimisticMeData);
      }

      // Call the original onSuccess if provided
      options?.onSuccess?.(data, cancel_at_period_end, context);
    },
  });
}

export function usePreviewStripeInvoice(
  options?: Partial<
    UseMutationOptions<
      PreviewStripeInvoiceResponse,
      AxiosError,
      PreviewStripeInvoicePayload
    >
  >,
) {
  return useMutation<
    PreviewStripeInvoiceResponse,
    AxiosError,
    PreviewStripeInvoicePayload
  >({
    mutationKey: ["previewStripeInvoice"],
    mutationFn: async (payload: PreviewStripeInvoicePayload) =>
      orgAPI.billing.previewStripeInvoice(payload),
    ...options,
  });
}
